import {
  Button,
  Input,
  InputProps,
  makeStyles,
} from "@fluentui/react-components";
import {
  bundleIcon,
  ChevronDownFilled,
  ChevronDownRegular,
  ChevronUpFilled,
  ChevronUpRegular,
} from "@fluentui/react-icons";
import { useEffect, useRef, useState } from "react";
import { $cloud70, $gray600, $sky60 } from "../../../../cssjs/colors";
import { getCyclicInputValue } from "../../../../utils/common/CommonHelperFunctions";

const ChevronDown = bundleIcon(ChevronDownFilled, ChevronDownRegular);
const ChevronUp = bundleIcon(ChevronUpFilled, ChevronUpRegular);

interface AmdNumberInputProps {
  value: number;
  setValue: (value: number) => void;
  maxValue?: number;
  padValue?: boolean;
}

const getDisplayValue = (value: number, padValue: boolean) => {
  const displayValue = value.toString();
  return padValue ? displayValue.padStart(2, "0") : displayValue;
};

const AmdNumberInput: React.FC<AmdNumberInputProps> = ({
  value,
  setValue,
  maxValue = 0,
  padValue = false,
}) => {
  const styles = useStyles();
  const inputRef = useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = useState<string>(
    getDisplayValue(value, padValue)
  );

  const onInputChangeHandler: InputProps["onChange"] = (_ev, data) => {
    const inputValue = data.value;
    // Allow empty input and negative sign at the start
    if (inputValue === "") {
      setInputValue(inputValue);
      return;
    }

    const numberValue = Number(inputValue);
    if (!isNaN(numberValue) && numberValue >= 0 && numberValue <= maxValue) {
      setInputValue(inputValue);
    }
  };

  const onInputBlurHandler: React.FocusEventHandler<HTMLInputElement> = (
    ev
  ) => {
    const inputValue = ev.target.value;

    const numberValue = Number(inputValue);
    if (inputValue === "" || Number.isNaN(numberValue)) {
      setInputValue(getDisplayValue(0, padValue));
      setValue(0);
    } else if (!isNaN(numberValue)) {
      setInputValue(getDisplayValue(numberValue, padValue));
      setValue(numberValue);
    }
  };

  useEffect(() => {
    // Update input value when the value prop changes
    setInputValue(getDisplayValue(value, padValue));
  }, [value, padValue]);

  useEffect(() => {
    // Handle wheel event for incrementing/decrementing value
    const handleWheel = (event: WheelEvent) => {
      event.preventDefault();
      let currentValue = parseInt((event.target as HTMLInputElement).value, 10);

      if (Number.isNaN(currentValue)) {
        currentValue = 0;
      }

      if (event.deltaY < 0) {
        const cyclicValue = getCyclicInputValue(0, maxValue, currentValue + 1);
        setInputValue(getDisplayValue(cyclicValue, padValue));
        setValue(cyclicValue);
      } else {
        const cyclicValue = getCyclicInputValue(0, maxValue, currentValue - 1);
        setInputValue(getDisplayValue(cyclicValue, padValue));
        setValue(cyclicValue);
      }
    };

    const inputElement = inputRef.current;
    if (inputElement) {
      inputElement.addEventListener("wheel", handleWheel, { passive: false });
    }

    return () => {
      if (inputElement) {
        inputElement.removeEventListener("wheel", handleWheel);
      }
    };
  }, [maxValue, padValue, setValue]);

  const handleIncrement = () => {
    const numberValue = Number(inputValue);
    const cyclicValue = getCyclicInputValue(0, maxValue, numberValue + 1);
    setInputValue(getDisplayValue(cyclicValue, padValue));
    setValue(cyclicValue);
  };

  const handleDecrement = () => {
    const numberValue = Number(inputValue);
    const cyclicValue = getCyclicInputValue(0, maxValue, numberValue - 1);
    setInputValue(getDisplayValue(cyclicValue, padValue));
    setValue(cyclicValue);
  };

  return (
    <div className={styles.container} data-testid="amd-number-input">
      <Button
        appearance="transparent"
        icon={<ChevronUp />}
        onClick={handleIncrement}
        className={styles.button}
      />
      <Input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={onInputChangeHandler}
        onBlur={onInputBlurHandler}
        className={styles.input}
      />
      <Button
        icon={<ChevronDown />}
        onClick={handleDecrement}
        appearance="transparent"
        className={styles.button}
      />
    </div>
  );
};

export default AmdNumberInput;

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  button: {
    color: $sky60,
  },
  input: {
    "& input": {
      textAlign: "center",
      padding: "0px",
      fontStyle: "italic",
      color: $gray600,
    },
    width: "42px",
    border: `2px solid ${$cloud70}`,
  },
});
